import { motion } from "framer-motion";
import { ArrowRight, MessagesSquare, Ticket, Rocket } from "lucide-react";
import { PageHeader, DISCORD_URL, SEO, fadeUp, cardReveal, staggerContainer } from "@/components/shared";
import { DiscordWidget, DiscordWidgetCompact } from "@/components/DiscordWidget";

const STEPS = [
  {
    icon: MessagesSquare,
    title: "Join the Discord",
    text: "Our community and development team are all in one place.",
    testid: "contact-step-join-discord",
  },
  {
    icon: Ticket,
    title: "Tell us your idea",
    text: "Share what you need — a plugin, a setup, or something entirely new.",
    testid: "contact-step-tell-us",
  },
  {
    icon: Rocket,
    title: "We build it",
    text: "Work directly with developers who bring your idea to life.",
    testid: "contact-step-we-build",
  },
];

export default function ContactPage() {
  return (
    <>
      <SEO
        title="Contact — Vexora Development"
        description="Reach Vexora Development through our Discord community. Join, say hello, and tell us what you want to build."
      />
      <PageHeader
        eyebrow="Contact"
        title={
          <>
            Let's{" "}
            <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-500 bg-clip-text text-transparent">
              talk
            </span>
          </>
        }
        subtitle="The fastest way to reach us is through our Discord community. Join, say hello, and tell us what you want to build."
      />

      <section data-testid="contact-steps-section" className="relative pb-24 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14"
          >
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                variants={cardReveal}
                data-testid={step.testid}
                className="group relative h-full rounded-2xl border border-purple-500/15 bg-[#11131f]/80 p-7 transition-all duration-300 hover:border-purple-500/50 hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgba(147,51,234,0.18)]"
              >
                <span className="absolute top-6 right-7 font-display text-4xl font-extrabold text-purple-500/15">
                  0{i + 1}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-purple-500/25 bg-purple-500/10 transition-colors duration-300 group-hover:bg-purple-500/20">
                  <step.icon className="w-5 h-5 text-purple-400" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.21, 0.65, 0.35, 1] }}
            className="mb-8"
          >
            <DiscordWidget />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.21, 0.65, 0.35, 1] }}
            className="relative overflow-hidden rounded-3xl border border-purple-500/25 bg-[#11131f] px-6 py-16 sm:px-12 text-center"
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 50% 0%, rgba(147,51,234,0.25) 0%, rgba(9,10,15,0) 65%)",
              }}
            />
            <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
            <div className="relative">
              <h2
                data-testid="contact-cta-heading"
                className="text-3xl sm:text-4xl font-bold tracking-tight text-white"
              >
                Join the conversation
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-base sm:text-lg text-slate-300">
                Click below to open your Discord invite — we'll see you inside.
              </p>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-join-discord-button"
                className="btn-glow mt-8 inline-flex items-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:shadow-[0_0_45px_rgba(168,85,247,0.75)]"
              >
                Join Our Discord
                <ArrowRight className="w-5 h-5" />
              </a>
              <p
                data-testid="contact-invite-url"
                className="mt-6 text-xs font-mono text-slate-500"
              >
                {DISCORD_URL}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.21, 0.65, 0.35, 1] }}
            className="mt-8"
          >
            <DiscordWidgetCompact />
          </motion.div>
        </div>
      </section>
    </>
  );
}
